/**
 * Navegação do layout Simplificado (spec 018, US4) — registro PURO: sem React,
 * sem alias `@/`, porque a guarda de navegação da API
 * (apps/api/src/shared/simplified-nav.test.ts) o importa para provar que TODA
 * rota do layout Completo tem destino alcançável aqui (AS1: nenhuma função
 * perdida).
 *
 * Medido em `5c943de`: 11 itens na barra lateral. Aqui são 7 destinos — Hoje,
 * Atendimentos, Conversas, Agenda, Clientes · Conhecimentos, Gestão ·
 * Configurações. Recursos, Contratos e Cofre viram ABAS de Clientes;
 * Relatórios e Apontamentos (e o painel antigo) vão para Gestão. As telas
 * existentes não mudam: montam dentro do shell simplificado, e a aba no topo
 * diz onde se está.
 */
import type { PermissionKey } from '@erp/shared';
import { NAV_ANY_OF } from './nav-permissions';

/** Rótulo fixo, ou o termo do perfil do tenant (017) — "Atendimentos", "Associações". */
export type NavLabel = string | { term: 'tickets' | 'clients' };

export interface NavTab {
  key: string;
  label: NavLabel;
  to: string;
  search?: Record<string, string>;
  /** some quando o /me veio sem NENHUMA destas keys */
  anyOf?: readonly PermissionKey[];
  /** paths (prefixo por segmento) desta aba */
  match: readonly string[];
  /** quando o path é de mais de uma aba (/tickets: Kanban × Pré-tickets) */
  matchSearch?: (search: Record<string, unknown>) => boolean;
}

export type DestinationKey =
  | 'hoje'
  | 'atendimentos'
  | 'conversas'
  | 'agenda'
  | 'clientes'
  | 'conhecimentos'
  | 'gestao'
  | 'configuracoes';

export interface NavDestination {
  key: DestinationKey;
  label: NavLabel;
  /** grupos separados por um divisor na barra */
  group: 1 | 2 | 3;
  /** paths do destino (a barra acende o item) */
  match: readonly string[];
  tabs: readonly NavTab[];
}

export const SIMPLE_DESTINATIONS: readonly NavDestination[] = [
  {
    key: 'hoje',
    label: 'Hoje',
    group: 1,
    match: ['/s/hoje'],
    tabs: [
      {
        key: 'hoje',
        label: 'Hoje',
        to: '/s/hoje',
        anyOf: ['tickets.view_open', 'chat.access'],
        match: ['/s/hoje'],
      },
    ],
  },
  {
    key: 'atendimentos',
    label: { term: 'tickets' },
    group: 1,
    // o detalhe completo (/tickets/:n?completo=1) e o Kanban também são daqui
    match: ['/s/atendimentos', '/tickets'],
    tabs: [
      { key: 'lista', label: 'Lista', to: '/s/atendimentos', anyOf: ['tickets.view_open'], match: ['/s/atendimentos'] },
      {
        key: 'kanban',
        label: 'Kanban',
        to: '/tickets',
        search: { view: 'board' },
        anyOf: ['tickets.view_open'],
        match: ['/tickets'],
        matchSearch: (s) => s['view'] === 'board',
      },
      {
        key: 'pretickets',
        label: 'Pré-tickets',
        to: '/tickets',
        search: { view: 'pretickets' },
        anyOf: ['pretickets.access'],
        match: ['/tickets'],
        matchSearch: (s) => s['view'] === 'pretickets',
      },
      // spec 025: os disparos do WhatsApp da regra de disparo das mesas
      {
        key: 'disparos',
        label: 'Disparos',
        to: '/tickets/dispatches',
        anyOf: ['tickets.view_open'],
        match: ['/tickets/dispatches'],
      },
    ],
  },
  {
    key: 'conversas',
    label: 'Conversas',
    group: 1,
    match: ['/s/conversas', '/chats', '/supervisao'],
    tabs: [
      { key: 'conversas', label: 'Conversas', to: '/s/conversas', anyOf: ['chat.access'], match: ['/s/conversas'] },
      // spec 071: o supervisor acompanha a equipe ao lado das próprias conversas
      { key: 'supervisao', label: 'Supervisão', to: '/supervisao', anyOf: NAV_ANY_OF.supervision, match: ['/supervisao'] },
    ],
  },
  {
    key: 'agenda',
    label: 'Agenda',
    group: 1,
    match: ['/agenda'],
    tabs: [{ key: 'agenda', label: 'Agenda', to: '/agenda', anyOf: NAV_ANY_OF.agenda, match: ['/agenda'] }],
  },
  {
    key: 'clientes',
    label: { term: 'clients' },
    group: 1,
    match: ['/clients', '/resources', '/end-customers', '/contracts', '/vault'],
    tabs: [
      { key: 'clientes', label: { term: 'clients' }, to: '/clients', anyOf: ['clients.view'], match: ['/clients'] },
      // spec 032: a página do associado abre a partir do veículo (o recurso)
      { key: 'recursos', label: 'Recursos', to: '/resources', anyOf: ['resources.view'], match: ['/resources', '/end-customers'] },
      // a tela lê /billing, e o GET de /billing é contracts.view (permission-map)
      { key: 'contratos', label: 'Contratos', to: '/contracts', anyOf: ['contracts.view'], match: ['/contracts'] },
      // spec 058: o Cofre é da equipe de tickets (cada cofre ainda tem permissão própria)
      { key: 'cofre', label: 'Cofre de senhas', to: '/vault', anyOf: NAV_ANY_OF.vault, match: ['/vault'] },
    ],
  },
  {
    key: 'conhecimentos',
    label: 'Conhecimentos',
    group: 2,
    match: ['/knowledge', '/novidades'],
    tabs: [
      { key: 'conhecimentos', label: 'Conhecimentos', to: '/knowledge', anyOf: NAV_ANY_OF.knowledge, match: ['/knowledge'] },
      // spec 036: o histórico das Novidades (o megafone do topo leva para cá, para todos)
      { key: 'novidades', label: 'Novidades', to: '/novidades', anyOf: NAV_ANY_OF.knowledge, match: ['/novidades'] },
    ],
  },
  {
    key: 'gestao',
    label: 'Gestão',
    group: 2,
    match: ['/s/painel', '/reports', '/appointments'],
    tabs: [
      { key: 'painel', label: 'Painel', to: '/s/painel', anyOf: NAV_ANY_OF.management, match: ['/s/painel'] },
      {
        key: 'relatorios',
        label: 'Relatórios',
        to: '/reports',
        anyOf: ['reports.admin', 'reports.attendants'],
        match: ['/reports'],
      },
      { key: 'apontamentos', label: 'Apontamentos', to: '/appointments', anyOf: NAV_ANY_OF.management, match: ['/appointments'] },
    ],
  },
  {
    key: 'configuracoes',
    label: 'Configurações',
    group: 3,
    match: ['/settings', '/account', '/notificacoes'],
    tabs: [
      { key: 'configuracoes', label: 'Configurações', to: '/settings', anyOf: NAV_ANY_OF.settings, match: ['/settings'] },
      // no Completo, só pelo menu do usuário (que o Simplificado também tem)
      { key: 'conta', label: 'Minha conta', to: '/account', match: ['/account'] },
      // spec 037: o histórico do sino (o "Ver notificações" leva para cá)
      { key: 'notificacoes', label: 'Notificações', to: '/notificacoes', match: ['/notificacoes'] },
    ],
  },
];

/** `/tickets` casa `/tickets` e `/tickets/12`, nunca `/ticketsx` */
export function pathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function destinationFor(pathname: string): NavDestination | null {
  // o prefixo mais longo vence (/s/atendimentos antes de /s)
  let best: { d: NavDestination; len: number } | null = null;
  for (const d of SIMPLE_DESTINATIONS) {
    for (const prefix of d.match) {
      if (pathMatches(pathname, prefix) && (!best || prefix.length > best.len)) best = { d, len: prefix.length };
    }
  }
  return best?.d ?? null;
}

export function tabFor(
  destination: NavDestination,
  pathname: string,
  search: Record<string, unknown>
): NavTab | null {
  return (
    destination.tabs.find(
      (t) => t.match.some((m) => pathMatches(pathname, m)) && (!t.matchSearch || t.matchSearch(search))
    ) ?? null
  );
}

/** Abas que a pessoa enxerga; `can` segue o usePermissions (allow-all sem lista). */
export function visibleTabs(destination: NavDestination, can: (key: PermissionKey) => boolean): NavTab[] {
  return destination.tabs.filter((t) => !t.anyOf || t.anyOf.some((k) => can(k)));
}

/**
 * A barra lateral (US4 AS2): só os destinos com ao menos uma aba permitida, e
 * o link de cada um é a PRIMEIRA aba permitida — nunca uma tela que a pessoa
 * não pode abrir.
 */
export function visibleDestinations(
  can: (key: PermissionKey) => boolean
): Array<{ destination: NavDestination; tabs: NavTab[] }> {
  return SIMPLE_DESTINATIONS.map((destination) => ({ destination, tabs: visibleTabs(destination, can) })).filter(
    (x) => x.tabs.length > 0
  );
}

// ── troca de layout ─────────────────────────────────────────────────────────
export type LayoutKind = 'classic' | 'simplified';

export interface LayoutRedirect {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

const TICKET_DETAIL = /^\/tickets\/(\d+)$/;
const SIMPLE_TICKET_DETAIL = /^\/s\/atendimentos\/(\d+)$/;
const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : typeof v === 'number' ? String(v) : undefined;

/**
 * Para onde a MESMA intenção vai no outro layout — usado no `beforeLoad` da
 * rota autenticada (link salvo, digitado, compartilhado entre pessoas com
 * layouts diferentes) e na troca pelo menu do usuário. `null` = a rota vale
 * nos dois layouts (as telas que o Simplificado reaproveita).
 *
 * Três portas ficam abertas de propósito no Simplificado: o Kanban e a
 * triagem de pré-tickets (`/tickets?view=board|pretickets`, abas de
 * Atendimentos) e o detalhe COMPLETO do ticket (`/tickets/:n?completo=1`,
 * "Abrir na visão completa") — nenhuma função do Completo fica sem caminho.
 */
export function layoutRedirect(
  layout: LayoutKind,
  pathname: string,
  search: Record<string, unknown>
): LayoutRedirect | null {
  if (layout === 'simplified') {
    if (pathname === '/') return { to: '/s/hoje' };
    if (pathname === '/tickets') {
      if (search['view'] === 'board' || search['view'] === 'pretickets') return null;
      // `?new=1` abre o Novo ticket por cima do quadro (o "Ver novidade" das
      // Novidades leva para lá, spec 036). Fechar o diálogo limpa o `new`, e o
      // `/tickets` puro volta para a lista do Simplificado por esta mesma função
      if (search['new'] !== undefined) return null;
      const q = str(search['q']);
      const desk = str(search['desk']);
      return {
        to: '/s/atendimentos',
        search: { ...(q ? { q } : {}), ...(desk && /^\d+$/.test(desk) ? { mesas: [Number(desk)] } : {}) },
      };
    }
    if (pathname === '/tickets/search') {
      const q = str(search['q']);
      return { to: '/s/atendimentos', search: { ...(q ? { q, status: 'all' } : {}) } };
    }
    const detail = TICKET_DETAIL.exec(pathname);
    if (detail && String(search['completo']) !== '1') {
      return { to: '/s/atendimentos/$ticketNumber', params: { ticketNumber: detail[1]! } };
    }
    if (pathname === '/chats') {
      const chat = str(search['chat']);
      return { to: '/s/conversas', search: chat ? { chat } : {} };
    }
    return null;
  }

  if (pathname === '/s/hoje' || pathname === '/s/painel') return { to: '/' };
  if (pathname === '/s/atendimentos') {
    const q = str(search['q']);
    return { to: '/tickets', search: q ? { q, view: 'list', status: 'all' } : {} };
  }
  const simpleDetail = SIMPLE_TICKET_DETAIL.exec(pathname);
  if (simpleDetail) return { to: '/tickets/$ticketNumber', params: { ticketNumber: simpleDetail[1]! } };
  if (pathname === '/s/conversas') {
    const chat = str(search['chat']);
    return { to: '/chats', search: chat ? { chat } : {} };
  }
  return null;
}
